import { useRef, useEffect } from "react";
import Portal from "@reach/portal";

const style = {
  body: `flex-shrink flex-grow p-5 md:p-8`,
  headerTitle: `font-sans font-extrabold leading-none text-1xl md:text-2xl font-bold mb-0`,
  content: `relative flex flex-col bg-white pointer-events-auto rounded-lg overflow-hidden shadow-lg`,
  header: `items-start justify-between p-5 md:p-8 pb-0 md:pb-0`,
  container: `fixed top-0 overflow-y-auto left-0 z-9999 w-full h-full m-0 bg-gray-300`,
  overlay: `fixed top-0 left-0 z-9999 w-screen h-screen bg-black`,
  footer: `flex flex-wrap items-center justify-end p-3 border-t border-gray-300 bg-gray-100`,
  orientation: `mt-12 mx-8 pb-6 md:m-auto md:w-9/12 lg:w-9/12 md:pt-12 outline-none`,
};

const Modal = ({ children, isOpen, toggle }) => {
  const modalDialogueRef = useRef();
  useEffect(() => {
    const handleEscape = (event) => {
      if (!isOpen) return;
      if (event.key === "Escape") {
        toggle(false);
      }
    };
    document.addEventListener("keyup", handleEscape);
    return () => document.removeEventListener("keyup", handleEscape);
  }, [isOpen, toggle]);

  useEffect(() => {
    if (!isOpen) return;

    modalDialogueRef.current?.focus();

    const html = document.documentElement;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    html.style.overflow = "hidden";
    html.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      html.style.overflow = "";
      html.style.paddingRight = "";
    };
  }, [isOpen]);

  return (
    <Portal>
      {isOpen && (
        <>
          <div className={style.overlay} />
          <div className={style.container}>
            <div
              aria-modal={true}
              className={style.orientation}
              ref={modalDialogueRef}
              tabIndex={-1}
            >
              <div className={style.content}>{children}</div>
            </div>
          </div>
        </>
      )}
    </Portal>
  );
};

const ModalHeader = ({ children }) => {
  return (
    <div className={style.header}>
      <h1 className={style.headerTitle}>{children}</h1>
    </div>
  );
};

const ModalBody = ({ children }) => {
  return <div className={style.body}>{children}</div>;
};

const ModalFooter = ({ children }) => {
  return <div className={style.footer}>{children}</div>;
};

export { Modal, ModalBody, ModalHeader, ModalFooter };
